/**
 * CustomerCard — sprite nhân vật full-body + speech bubble + patience bar.
 *
 * Sprite: PNG transparent từ `/assets/customers/<code>.png` (resize ~420px tall).
 * Patience bar update mỗi 100ms từ `patience_deadline`.
 *
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-06.
 *   - design/5. Thiết kế nhân vật khách hàng Phase 1_*.png.
 */
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import type { CustomerCode } from "../types";

interface Props {
  customerType: CustomerCode;
  dialogue: string;
  requestedRecipeDisplay: string;
  patienceDeadline: string;
}

const CUSTOMER_LABEL: Record<CustomerCode, string> = {
  student: "Sinh viên",
  office_worker: "Nhân viên văn phòng",
  shipper: "Shipper",
};

const CUSTOMER_SPRITE: Record<CustomerCode, string> = {
  student: "/assets/customers/student.png",
  office_worker: "/assets/customers/office_worker.png",
  shipper: "/assets/customers/shipper.png",
};

const TICK_MS = 100;

export function CustomerCard({
  customerType,
  dialogue,
  requestedRecipeDisplay,
  patienceDeadline,
}: Props) {
  const deadlineMs = new Date(patienceDeadline).getTime();
  const totalMs = Math.max(1, deadlineMs - Date.now());

  const [remainingPct, setRemainingPct] = useState(() => {
    const remain = Math.max(0, deadlineMs - Date.now());
    return (remain / totalMs) * 100;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, deadlineMs - Date.now());
      setRemainingPct((remaining / totalMs) * 100);
      if (remaining <= 0) clearInterval(interval);
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [deadlineMs, totalMs]);

  const barColor =
    remainingPct > 50
      ? "bg-leaf"
      : remainingPct > 20
        ? "bg-amber50"
        : "bg-stool";

  return (
    <motion.div
      data-testid="customer-card"
      initial={{ x: 60, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="relative max-w-md w-full"
    >
      <div className="flex items-end gap-3">
        {/* Sprite nhân vật */}
        <motion.img
          key={customerType}
          src={CUSTOMER_SPRITE[customerType]}
          alt={CUSTOMER_LABEL[customerType]}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="w-28 sm:w-32 h-auto select-none pointer-events-none drop-shadow-lg"
          loading="eager"
        />

        {/* Speech bubble */}
        <div className="flex-1 min-w-0 bg-white rounded-2xl p-3 shadow-soft-amber relative">
          {/* Tail trỏ về sprite */}
          <div className="absolute -left-2 bottom-4 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-white" />

          <div
            data-testid="customer-type"
            className="text-xs font-medium text-coffee/60"
          >
            {CUSTOMER_LABEL[customerType]}
          </div>
          <div
            data-testid="customer-dialogue"
            className="text-sm text-coffee leading-snug mt-1"
          >
            “{dialogue}”
          </div>
          <div className="text-xs text-coffee/70 mt-2">
            Món yêu cầu:{" "}
            <span
              className="font-semibold text-coffee"
              data-testid="customer-recipe"
            >
              {requestedRecipeDisplay}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 h-1.5 bg-white/70 rounded-full overflow-hidden">
        <div
          data-testid="patience-bar"
          className={`h-full transition-all duration-100 ease-linear ${barColor}`}
          style={{ width: `${remainingPct}%` }}
        />
      </div>
    </motion.div>
  );
}
